import {zodResolver} from '@hookform/resolvers/zod';
import {LocalDate} from '@js-joda/core';
import {CircularProgress} from '@mui/material';
import {type Dayjs, isDayjs} from 'dayjs';
import {type ReactNode, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {z} from 'zod';
import DisplayIssues from '@/components/OnBoarding/DisplayIssues.tsx';
import OnBoardingForm from '@/components/OnBoarding/OnBoardingForm.tsx';
import {type IssueCreateResponse, useCreateOnboarding} from '@/Hooks/useCreateIssue.ts';

export type FieldValues = {
    associateType: string;
    firstName: string;
    lastName: string;
    startDate: Dayjs;
    groups: string[];
    computerPreference?: string;
};

export const dayjsToLocalDate = (date: Dayjs): LocalDate =>
    LocalDate.of(date.year(), date.month() + 1, date.date());
export const dayjsLocalDateSchema = z.custom<Dayjs>(isDayjs).transform(dayjsToLocalDate);

const onBoardingSchema = z.object({
    associateType: z.string(),
    firstName: z.string().min(1, {message: 'First Name is required'}),
    lastName: z.string().min(1, {message: 'Last Name is required'}),
    startDate: dayjsLocalDateSchema,
    groups: z.array(z.string()).min(0),
    computerPreference: z.string().optional(),
});

export type OnBoardingSchemaType = z.infer<typeof onBoardingSchema>;

const OnBoarding = (): ReactNode => {
    const [running, setRunning] = useState<boolean>(false);
    const [issues, setIssues] = useState<IssueCreateResponse[]>([]);
    const form = useForm<FieldValues, any, OnBoardingSchemaType>({
        resolver: zodResolver(onBoardingSchema),
    });
    const createIssues = useCreateOnboarding();

    return (
        <FormProvider {...form}>
            <form
                onSubmit={form.handleSubmit(async data => {
                    setIssues([]);
                    setRunning(true);
                    createIssues(data)
                        .then(issues => {
                            setIssues(issues);
                            form.reset();
                        })
                        .finally(() => setRunning(false));
                })}
            >
                <OnBoardingForm />
            </form>
            {running && <CircularProgress />}
            {issues && <DisplayIssues issues={issues} />}
        </FormProvider>
    );
};

export default OnBoarding;
